'use client';

import React from 'react';
import {
  CalendarDays, MapPin, Users, CalendarCheck,
  ExternalLink, Edit, BarChart3, Target, Clock, ArrowRight
} from 'lucide-react';
import { EventRecord } from './mockEvents';
import { toast } from 'sonner';

interface Props {
  event: EventRecord;
}

const statusStyle: Record<string, string> = {
  active: 'event-active',
  planned: 'event-planned',
  completed: 'event-completed',
  archived: 'event-archived',
};

const statusLabel: Record<string, string> = {
  active: 'Active',
  planned: 'Planned',
  completed: 'Completed',
  archived: 'Archived',
};

const priorityDot: Record<string, string> = {
  High: 'bg-red-500',
  Medium: 'bg-amber-500',
  Low: 'bg-slate-400',
};

const typeColor: Record<string, string> = {
  'Conference': 'bg-indigo-50 text-indigo-700',
  'Trade Show': 'bg-blue-50 text-blue-700',
  'Expo': 'bg-cyan-50 text-cyan-700',
  'Summit': 'bg-violet-50 text-violet-700',
  'Forum': 'bg-purple-50 text-purple-700',
  'Exhibition': 'bg-sky-50 text-sky-700',
};

function formatDateRange(start: string, end: string): string {
  const s = new Date(start);
  const e = new Date(end);
  const opts: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };
  const startStr = s.toLocaleDateString('en-US', opts);
  const endStr = e.toLocaleDateString('en-US', { ...opts, year: 'numeric' });
  return `${startStr} – ${endStr}`;
}

function PipelineBar({ event }: { event: EventRecord }) {
  const stages = [
    { label: 'LinkedIn', value: event.linkedinConnected, max: event.totalContacts, color: 'bg-sky-400' },
    { label: 'Mtg Req', value: event.meetingRequests, max: event.totalContacts, color: 'bg-amber-400' },
    { label: 'Confirmed', value: event.meetingsConfirmed, max: event.totalContacts, color: 'bg-emerald-500' },
  ];

  return (
    <div className="space-y-1.5">
      {stages.map((s) => (
        <div key={`pb-${s.label}-${event.id}`} className="flex items-center gap-2">
          <span className="text-[10px] text-muted-foreground w-16 shrink-0">{s.label}</span>
          <div className="flex-1 bg-muted rounded-full h-1.5 overflow-hidden">
            <div
              className={`h-full rounded-full ${s.color} transition-all duration-700`}
              style={{ width: `${Math.min((s.value / s.max) * 100, 100)}%` }}
            />
          </div>
          <span className="text-[10px] font-mono font-semibold text-foreground w-8 text-right shrink-0">
            {s.value}
          </span>
        </div>
      ))}
    </div>
  );
}

export default function EventCard({ event }: Props) {
  const isUpcoming = event.daysUntil > 0;
  const isCompleted = event.status === 'completed';
  const meetingRate = event.meetingRequests > 0
    ? Math.round((event.meetingsConfirmed / event.meetingRequests) * 100)
    : 0;

  return (
    <div className="bg-white rounded-xl border border-border shadow-card hover:shadow-card-hover transition-all duration-200 flex flex-col overflow-hidden group">
      {/* Header */}
      <div className={`px-5 pt-5 pb-4 border-b border-border ${isCompleted ? 'bg-muted/30' : ''}`}>
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1.5">
              <span className={`badge ${statusStyle[event.status]}`}>
                {statusLabel[event.status]}
              </span>
              <span className={`badge ${typeColor[event.type] ?? 'bg-gray-100 text-gray-600'}`}>
                {event.type}
              </span>
              <div className={`w-1.5 h-1.5 rounded-full ${priorityDot[event.priority]} ml-auto shrink-0`} title={`${event.priority} priority`} />
            </div>
            <h3 className="text-sm font-semibold text-foreground leading-snug">{event.name}</h3>
          </div>
        </div>

        {/* Meta row */}
        <div className="flex flex-wrap items-center gap-3 text-[11px] text-muted-foreground">
          <span className="flex items-center gap-1">
            <CalendarDays size={11} />
            {formatDateRange(event.startDate, event.endDate)}
          </span>
          <span className="flex items-center gap-1">
            <MapPin size={11} />
            {event.city}, {event.country}
          </span>
        </div>

        {/* Countdown / elapsed */}
        {isUpcoming ? (
          <div className="mt-3 flex items-center gap-1.5 px-2.5 py-1.5 bg-amber-50 border border-amber-200 rounded-lg w-fit">
            <Clock size={11} className="text-amber-600" />
            <span className="text-[11px] font-semibold text-amber-800">
              {event.daysUntil} days until show opens
            </span>
          </div>
        ) : (
          <div className="mt-3 flex items-center gap-1.5 px-2.5 py-1.5 bg-muted rounded-lg w-fit">
            <CalendarCheck size={11} className="text-muted-foreground" />
            <span className="text-[11px] text-muted-foreground">
              Completed {Math.abs(event.daysUntil)} days ago
            </span>
          </div>
        )}
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-4 divide-x divide-border border-b border-border">
        {[
          { label: 'Leads', value: event.totalContacts, icon: Users, color: 'text-primary' },
          { label: 'Connected', value: event.linkedinConnected, icon: Users, color: 'text-sky-600' },
          { label: 'Meetings', value: event.meetingsConfirmed, icon: CalendarCheck, color: 'text-emerald-600' },
          { label: 'Opps', value: event.opportunities, icon: Target, color: 'text-orange-600' },
        ].map((stat) => {
          const Icon = stat.icon;
          return (
            <div key={`stat-${stat.label}-${event.id}`} className="flex flex-col items-center py-3 px-2">
              <span className={`text-lg font-bold tabular-nums font-mono ${stat.color}`}>{stat.value}</span>
              <span className="text-[9px] font-medium uppercase tracking-wide text-muted-foreground mt-0.5">{stat.label}</span>
            </div>
          );
        })}
      </div>

      {/* Pipeline */}
      <div className="px-5 py-4 flex-1">
        <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground mb-3">
          Outreach Pipeline
        </p>
        <PipelineBar event={event} />

        {/* Meeting conversion */}
        {event.meetingRequests > 0 && (
          <div className="mt-3 flex items-center justify-between pt-3 border-t border-border">
            <span className="text-[11px] text-muted-foreground">Meeting conversion rate</span>
            <span className={`text-xs font-mono font-semibold ${meetingRate >= 40 ? 'text-emerald-600' : meetingRate >= 25 ? 'text-amber-600' : 'text-red-600'}`}>
              {meetingRate}%
            </span>
          </div>
        )}

        {/* Follow-up warning */}
        {event.followUpPending > 0 && (
          <div className="mt-2 flex items-center gap-1.5 text-[11px] text-amber-700 bg-amber-50 px-2.5 py-1.5 rounded-lg">
            <Clock size={11} />
            <span><strong className="font-semibold">{event.followUpPending}</strong> follow-ups pending</span>
          </div>
        )}
      </div>

      {/* Footer actions */}
      <div className="px-5 py-3 border-t border-border bg-muted/20 flex items-center justify-between gap-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
        <div className="flex items-center gap-1">
          <button
            onClick={() => toast.info(`Opening ${event.name} dashboard`)}
            className="btn-ghost text-[11px] py-1.5 px-2.5"
          >
            <BarChart3 size={12} />
            Dashboard
          </button>
          <button
            onClick={() => toast.info(`Editing ${event.name}`)}
            className="btn-ghost text-[11px] py-1.5 px-2.5"
          >
            <Edit size={12} />
            Edit
          </button>
          <a
            href={event.website}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.preventDefault()}
            className="btn-ghost text-[11px] py-1.5 px-2.5"
          >
            <ExternalLink size={12} />
            Website
          </a>
        </div>
        <button
          onClick={() => toast.info(`Opening contacts for ${event.name}`)}
          className="flex items-center gap-1 text-[11px] font-medium text-primary hover:text-primary/80 transition-colors"
        >
          View contacts
          <ArrowRight size={11} />
        </button>
      </div>
    </div>
  );
}